import React, { useEffect } from 'react';
import { getSpecifications, isVideoContent, formatDuration, PLATFORMS, CONTENT_TYPES } from '../../utils/platformConfig';
import { useLanguage } from '../../contexts/LanguageContext';

const SpecificationsForm = ({ 
  platform, 
  contentType, 
  specifications, 
  onChange,
  disabled = false 
}) => {
  const { isRTL } = useLanguage();
  
  const specs = getSpecifications(platform, contentType);
  const isVideo = isVideoContent(contentType);
  
  // Update specifications when platform/contentType changes
  useEffect(() => {
    if (specs && specs.aspectRatios?.length > 0) {
      const defaultRatio = specs.aspectRatios.find(ar => ar.recommended) || specs.aspectRatios[0];
      
      // Only set defaults if not already set
      if (!specifications.aspect_ratio) {
        onChange({
          ...specifications,
          aspect_ratio: defaultRatio.id,
          width: defaultRatio.width,
          height: defaultRatio.height,
        });
      }
    }
  }, [platform, contentType]);
  
  if (!platform || !contentType) {
    return (
      <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
        <p>{isRTL ? 'يرجى اختيار المنصة ونوع المحتوى' : 'Please select platform and content type first'}</p>
      </div>
    );
  }
  
  if (!specs) {
    return (
      <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
        <p>{isRTL ? 'لا توجد مواصفات متاحة' : 'No specifications available'}</p>
      </div>
    );
  }

  const handleAspectRatioChange = (ratioId) => {
    const ratio = specs.aspectRatios?.find(ar => ar.id === ratioId);
    onChange({
      ...specifications,
      aspect_ratio: ratioId,
      width: ratio?.width || specifications.width,
      height: ratio?.height || specifications.height,
    });
  };

  const handleChange = (field, value) => {
    onChange({
      ...specifications,
      [field]: value,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-2xl">{PLATFORMS[platform]?.icon}</span>
        <span className="text-2xl">{CONTENT_TYPES[contentType]?.icon}</span>
        <div>
          <h3 className="font-medium text-gray-900">
            {isRTL ? 'مواصفات المحتوى' : 'Content Specifications'}
          </h3>
          <p className="text-sm text-gray-500">
            {isRTL 
              ? `${PLATFORMS[platform]?.nameAr} - ${CONTENT_TYPES[contentType]?.nameAr}`
              : `${PLATFORMS[platform]?.name} - ${CONTENT_TYPES[contentType]?.name}`
            }
          </p>
        </div>
      </div>

      {/* Aspect Ratio Selection */}
      {specs.aspectRatios && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {isRTL ? 'نسبة العرض للارتفاع' : 'Aspect Ratio'} <span className="text-red-500">*</span>
          </label>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {specs.aspectRatios.map((ratio) => {
              const isSelected = specifications.aspect_ratio === ratio.id;
              
              return (
                <button
                  key={ratio.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleAspectRatioChange(ratio.id)}
                  className={`
                    relative p-3 rounded-lg border-2 transition-all
                    ${
                      isSelected 
                        ? 'border-sky-500 bg-sky-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-sky-200 hover:bg-sky-50/60'
                    }
                    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {/* Aspect Ratio Preview */}
                  <div className="flex justify-center mb-2">
                    <AspectRatioPreview ratio={ratio.id} selected={isSelected} />
                  </div>
                  
                  <div className="text-center">
                    <p className={`text-sm font-medium ${isSelected ? 'text-sky-700' : 'text-gray-700'}`}>
                      {isRTL ? ratio.nameAr : ratio.name}
                    </p>
                    {ratio.width && ratio.height && (
                      <p className="text-xs text-gray-500 mt-1">
                        {ratio.width} × {ratio.height}
                      </p>
                    )}
                    {ratio.recommended && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                        {isRTL ? 'موصى به' : 'Recommended'}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom Dimensions (if custom ratio) */}
      {specifications.aspect_ratio === 'custom' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'العرض (بكسل)' : 'Width (px)'}
            </label>
            <input
              type="number"
              value={specifications.width || ''}
              onChange={(e) => handleChange('width', parseInt(e.target.value) || 0)}
              className="input"
              placeholder="1080"
              disabled={disabled}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? 'الارتفاع (بكسل)' : 'Height (px)'}
            </label>
            <input
              type="number"
              value={specifications.height || ''}
              onChange={(e) => handleChange('height', parseInt(e.target.value) || 0)}
              className="input"
              placeholder="1080"
              disabled={disabled}
            />
          </div>
        </div>
      )}

      {/* Video Duration */}
      {isVideo && specs.maxDuration && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {isRTL ? 'مدة الفيديو (بالثواني)' : 'Video Duration (seconds)'}
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={specs.minDuration || 1}
              max={specs.maxDuration}
              value={specifications.duration_seconds || specs.minDuration || 15}
              onChange={(e) => handleChange('duration_seconds', parseInt(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              disabled={disabled}
            />
            <span className="text-sm font-medium text-gray-700 min-w-[60px] text-center">
              {formatDuration(specifications.duration_seconds || specs.minDuration || 15)}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {isRTL 
              ? `الحد: ${formatDuration(specs.minDuration || 0)} - ${formatDuration(specs.maxDuration)}`
              : `Range: ${formatDuration(specs.minDuration || 0)} - ${formatDuration(specs.maxDuration)}`
            }
          </p>
        </div>
      )}

      {/* Carousel Slides */}
      {contentType === 'carousel' && specs.maxSlides && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {isRTL ? 'عدد الشرائح' : 'Number of Slides'}
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={specs.minSlides || 2}
              max={specs.maxSlides}
              value={specifications.slides_count || specs.minSlides || 2}
              onChange={(e) => handleChange('slides_count', parseInt(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              disabled={disabled}
            />
            <span className="text-sm font-medium text-gray-700 min-w-[60px] text-center">
              {specifications.slides_count || specs.minSlides || 2} {isRTL ? 'شريحة' : 'slides'}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {isRTL 
              ? `الحد: ${specs.minSlides || 2} - ${specs.maxSlides} شريحة`
              : `Range: ${specs.minSlides || 2} - ${specs.maxSlides} slides`
            }
          </p>
        </div>
      )}

      {/* Specifications Summary */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          {isRTL ? 'ملخص المواصفات' : 'Specifications Summary'}
        </h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">{isRTL ? 'الحد الأقصى للملف:' : 'Max file size:'}</span>
            <span className="font-medium">{specs.maxFileSize} MB</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">{isRTL ? 'الصيغ المدعومة:' : 'Formats:'}</span>
            <span className="font-medium">{specs.formats?.join(', ')}</span>
          </div>
          {specs.captionMaxLength && (
            <div className="flex justify-between">
              <span className="text-gray-500">{isRTL ? 'حد الكابشن:' : 'Caption limit:'}</span>
              <span className="font-medium">{specs.captionMaxLength.toLocaleString()} {isRTL ? 'حرف' : 'chars'}</span>
            </div>
          )}
          {specs.hashtagsLimit && (
            <div className="flex justify-between">
              <span className="text-gray-500">{isRTL ? 'حد الهاشتاقات:' : 'Hashtags limit:'}</span>
              <span className="font-medium">{specs.hashtagsLimit}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Aspect Ratio Preview Component
const AspectRatioPreview = ({ ratio, selected }) => {
  const getPreviewDimensions = (ratio) => {
    const maxSize = 40;
    switch (ratio) {
      case '1:1':
        return { width: maxSize, height: maxSize };
      case '4:5':
        return { width: maxSize * 0.8, height: maxSize };
      case '9:16':
        return { width: maxSize * 0.5625, height: maxSize };
      case '16:9':
        return { width: maxSize, height: maxSize * 0.5625 };
      case '1.91:1':
        return { width: maxSize, height: maxSize * 0.52 };
      case '2:1':
        return { width: maxSize, height: maxSize * 0.5 };
      case '3:1':
        return { width: maxSize, height: maxSize * 0.33 };
      case '4:1':
        return { width: maxSize, height: maxSize * 0.25 };
      case '2.7:1':
        return { width: maxSize, height: maxSize * 0.37 };
      case '21:9':
        return { width: maxSize, height: maxSize * 0.43 };
      default:
        return { width: maxSize * 0.75, height: maxSize * 0.75 };
    }
  };

  const { width, height } = getPreviewDimensions(ratio);

  return (
    <div 
      className={`
        rounded border-2 
        ${selected ? 'border-primary-400 bg-primary-200' : 'border-gray-300 bg-gray-100'}
      `}
      style={{ width: `${width}px`, height: `${height}px` }}
    />
  );
};

export default SpecificationsForm;







